import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  Panel,
  applyNodeChanges,
  applyEdgeChanges,
  getNodesBounds,
  useReactFlow,
  useUpdateNodeInternals,
  getViewportForBounds,
  EdgeTypes,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "@dagrejs/dagre";
import { useParams } from "react-router-dom";
import theme from "../../../../theme";
import { useOrganisationData } from "../../../../data/useOrganisationData";
import { useRetrieveProfile } from "../../../../data/useRetrieveProfile";
import { Slider } from "../../../../components/Slider";
import { Flex } from "../../../../components/Flex";
import { Loader } from "../../../../components/Loader";
import { Button } from "../../../../components/Button";
import {
  FaArrowDown,
  FaArrowLeft,
  FaArrowRight,
  FaArrowUp,
  FaDownload,
  FaSlidersH,
} from "react-icons/fa";
import { CustomNode } from "../customTypes";
import { ProfileTab } from "../../ProfileTab";
import { Box } from "../../../../components/Box";
import { toPng } from "html-to-image";
import CustomEdge from "./customEdge";
import { DateTime } from "luxon";
import { showError } from "../../../../utils/error-handling";
import { OrgChartMenuContext } from "../OrgChartMenuContext";
import RelationshipSlider from "./RelationshipSlider";
import { useGetProfileRelationTypes } from "../../../../data/useGetProfileRelationTypes";
import {
  ChartControlsSidebar,
  DEFAULT_LOAD_FILTERS,
  DEFAULT_SHOW_FILTERS,
  hasActiveLoadFilters,
  hasActiveShowFilters,
  type LoadFilters,
  type ShowFilters,
} from "../ChartControlsSidebar";

/**
 * Layout direction, expressed as dagre rankdir values.
 *
 * On an Edge, source is the company and target is the related party, so dagre
 * places the company in the earlier rank. BT therefore puts related parties
 * above the company they relate to, which is how an organisational chart is
 * normally read, and is the default.
 */
type Orientation = "BT" | "TB" | "LR" | "RL";

const DEFAULT_ORIENTATION: Orientation = "BT";

const ORIENTATIONS: {
  value: Orientation;
  label: string;
  Icon: any;
}[] = [
  { value: "BT", label: "Above", Icon: FaArrowUp },
  { value: "TB", label: "Below", Icon: FaArrowDown },
  { value: "RL", label: "Left", Icon: FaArrowLeft },
  { value: "LR", label: "Right", Icon: FaArrowRight },
];

/** which side of the card each handle sits on, per layout direction */
const HANDLE_POSITIONS: Record<
  Orientation,
  { source: string; target: string }
> = {
  TB: { source: "bottom", target: "top" },
  BT: { source: "top", target: "bottom" },
  LR: { source: "right", target: "left" },
  RL: { source: "left", target: "right" },
};

/** matches the stroke in styles.css so the arrowhead is not a different colour */
const EDGE_COLOUR = "#2B2B2B";

/**
 * Applies the client-side filters to an already-laid-out graph.
 *
 * Elements are hidden in place rather than removed, so the layout does not
 * reflow and the structure stays where the user was reading it. Hiding a node
 * therefore leaves a gap, which is the intended trade: a stable picture is worth
 * more than a tightly packed one when comparing against what was on screen a
 * moment ago.
 */
const applyShowFilters = (
  nodes: any[],
  edges: any[],
  filters: ShowFilters,
  focusNodeId: string | null
) => {
  const hiddenNodeIds = new Set<string>();

  nodes.forEach((node) => {
    // the focus profile always stays: hiding what the chart is centred on would
    // leave the user without an anchor
    if (node.id === focusNodeId) return;

    const typeKey = node.data?.icon === "entity" ? "entity" : "individual";

    if (
      filters.profileTypes.length &&
      !filters.profileTypes.includes(typeKey)
    ) {
      hiddenNodeIds.add(node.id);
      return;
    }

    if (
      filters.profileStatuses.length &&
      !filters.profileStatuses.includes(node.data?.profile_status)
    ) {
      hiddenNodeIds.add(node.id);
    }
  });

  return {
    nodes: nodes.map((node) => ({
      ...node,
      hidden: hiddenNodeIds.has(node.id),
    })),
    edges: edges.map((edge) => ({
      ...edge,
      hidden: Boolean(
        hiddenNodeIds.has(edge.source) ||
          hiddenNodeIds.has(edge.target) ||
          (!filters.includeEnded && edge.data?.isEnded)
      ),
    })),
  };
};

type ChartToolbarProps = {
  orientation: Orientation;
  onOrientationChange: (orientation: Orientation) => void;
  nodeIds: string[];
  focusProfileName?: string;
};

/** strips characters that are not legal in a filename on Windows or macOS */
const sanitiseFileName = (name: string) =>
  name
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .trim();

const ChartToolbar = ({
  orientation,
  onOrientationChange,
  nodeIds,
  focusProfileName,
}: ChartToolbarProps) => {
  const { getNodes } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const [isExporting, setIsExporting] = useState(false);

  /*
   * The handles move to different sides of the card when the orientation
   * changes, so React Flow has to re-measure them or the edges keep routing to
   * where the handles used to be. This hook has to be called from inside the
   * ReactFlow tree, which is why it lives on the toolbar rather than the tab.
   */
  useEffect(() => {
    if (nodeIds.length) {
      updateNodeInternals(nodeIds);
    }
  }, [orientation, nodeIds, updateNodeInternals]);

  function downloadImage(dataUrl: string) {
    const a = document.createElement("a");
    const timestamp = DateTime.now().toFormat("yyyy-MM-dd_HH-mm-ss");
    const safeName = focusProfileName ? sanitiseFileName(focusProfileName) : "";
    const fileName = safeName
      ? `${safeName} - Org Chart - ${timestamp}.png`
      : `Org Chart - ${timestamp}.png`;
    a.setAttribute("download", fileName);
    a.setAttribute("href", dataUrl);
    a.click();
  }

  const imageWidth = 1400;
  const imageHeight = 900;

  const onClickDownload = () => {
    const viewportEl = document.querySelector(
      ".react-flow__viewport"
    ) as HTMLElement | null;

    if (!viewportEl) {
      showError(null, "Could not export the chart. Please try again.");
      return;
    }

    const nodesBounds = getNodesBounds(getNodes());
    const viewport = getViewportForBounds(
      nodesBounds,
      imageWidth,
      imageHeight,
      0.5,
      2,
      0
    );

    setIsExporting(true);
    toPng(viewportEl, {
      backgroundColor: theme.colors.white,
      width: imageWidth,
      height: imageHeight,
      style: {
        width: String(imageWidth),
        height: String(imageHeight),
        transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
      },
      skipFonts: true,
    })
      .then(downloadImage)
      .catch((err) =>
        showError(err, "Could not export the chart. Please try again.")
      )
      .finally(() => setIsExporting(false));
  };

  return (
    <Panel position="top-right">
      <Flex alignItems={"center"} gap="8">
        {/* A compact segmented control rather than four full-height buttons.
            Plain buttons because the design-system Button does not accept title
            or aria-pressed, both of which this needs: the control is icon only,
            so it relies on tooltips and on announcing which is active. */}
        <div
          className="org-orientation"
          role="group"
          aria-label="Chart orientation"
        >
          <span className="org-orientation-label">Related parties</span>
          {ORIENTATIONS.map(({ value, label, Icon }) => (
            <button
              key={value}
              type="button"
              className={`org-orientation-option${
                orientation === value ? " is-active" : ""
              }`}
              title={`Related parties ${label.toLowerCase()}`}
              aria-label={`Related parties ${label.toLowerCase()}`}
              aria-pressed={orientation === value}
              onClick={() => onOrientationChange(value)}
            >
              <Icon size={12} />
            </button>
          ))}
        </div>
        <Button
          Icon={FaDownload}
          iconSize="16px"
          onClick={onClickDownload}
          isDisabled={isExporting}
        >
          {isExporting ? "Preparing..." : "Download"}
        </Button>
      </Flex>
    </Panel>
  );
};

export const OrgChartTab = () => {
  const { id } = useParams<{ id: string }>();
  const profileId = Number(id);

  const validProfileId = Number.isFinite(profileId) ? profileId : 0;

  /*
   * Two separate pieces of filter state, deliberately behaving differently.
   * Load filters form part of the query, so changing one refetches. Show filters
   * are applied to the graph already in hand and cost no request.
   */
  const [loadFilters, setLoadFilters] =
    useState<LoadFilters>(DEFAULT_LOAD_FILTERS);
  const [showFilters, setShowFilters] =
    useState<ShowFilters>(DEFAULT_SHOW_FILTERS);

  // open by default; the toggle lives on the whiteboard
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const orgQuery = useOrganisationData(validProfileId, loadFilters);
  const fetchedorg = orgQuery.data;

  const { data: relationshipTypes } = useGetProfileRelationTypes();

  const [nodes, setNodes] = useState<any[]>([]);
  const [edges, setEdges] = useState<any[]>([]);

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(
    null
  );

  // held in state so the choice survives a refetch. It used to be hard-coded at
  // the layout call, which meant any refetch silently reverted to vertical.
  const [orientation, setOrientation] =
    useState<Orientation>(DEFAULT_ORIENTATION);

  // one open menu across the whole chart, and one shared Add Relationship pane
  const [openMenuNodeId, setOpenMenuNodeId] = useState<string | null>(null);
  const [relationshipProfile, setRelationshipProfile] = useState<any | null>(
    null
  );

  // callback ref, because the chart is conditionally rendered and a plain ref
  // would still be null when the measuring effect first runs
  const [chartEl, setChartEl] = useState<HTMLDivElement | null>(null);
  const [chartHeight, setChartHeight] = useState<number | null>(null);

  // Fill the space left below the page chrome instead of guessing at a
  // viewport fraction: 70vh left a gap at the bottom and 90vw ignored the
  // sidebar. Measured, so it stays right if the chrome above it changes.
  useEffect(() => {
    if (!chartEl) return;

    const BOTTOM_GUTTER = 24;
    const MIN_HEIGHT = 420;

    const measure = () => {
      const { top } = chartEl.getBoundingClientRect();
      // a hidden tab measures as zero; keep the last good value
      if (top <= 0) return;
      setChartHeight(
        Math.max(window.innerHeight - top - BOTTOM_GUTTER, MIN_HEIGHT)
      );
    };

    measure();
    window.addEventListener("resize", measure);

    // width changes when the tab is switched back into view, which is exactly
    // when a re-measure is needed. Setting height cannot change width, so this
    // will not loop.
    const observer = new ResizeObserver(measure);
    observer.observe(chartEl);

    return () => {
      window.removeEventListener("resize", measure);
      observer.disconnect();
    };
  }, [chartEl]);

  const closeMenu = useCallback(() => setOpenMenuNodeId(null), []);
  const openMenu = useCallback(
    (nodeId: string) => setOpenMenuNodeId(nodeId),
    []
  );
  const toggleMenu = useCallback(
    (nodeId: string) =>
      setOpenMenuNodeId((current) => (current === nodeId ? null : nodeId)),
    []
  );
  const openAddRelationship = useCallback((nodeData: any) => {
    setOpenMenuNodeId(null);
    setRelationshipProfile(nodeData);
  }, []);

  const menuContextValue = useMemo(
    () => ({
      openNodeId: openMenuNodeId,
      openMenu,
      toggleMenu,
      closeMenu,
      openAddRelationship,
    }),
    [openMenuNodeId, openMenu, toggleMenu, closeMenu, openAddRelationship]
  );

  // Escape closes the menu. React Flow's d3 handlers swallow mousedown over the
  // pane, so pane dismissal is wired through onPaneClick below rather than a
  // document listener.
  useEffect(() => {
    if (!openMenuNodeId) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenMenuNodeId(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [openMenuNodeId]);

  const selectedProfileQuery = useRetrieveProfile(
    selectedProfileId ?? undefined,
    { enabled: isDetailsOpen && !!selectedProfileId }
  );
  const selectedProfile = selectedProfileQuery.data;

  /*
   * Matched to the rendered card rather than guessed. .org-node is 300px wide
   * with 15px padding and no global box-sizing, so it occupies 330px. Height is
   * the tallest case today: an entity card with an icon row, a registration
   * number, an entity type and a status row.
   */
  const nodeWidth = 330;
  const nodeHeight = 150;

  const getLayoutedElements = useCallback(
    (inNodes: any[], inEdges: any[], direction: Orientation) => {
      const handles = HANDLE_POSITIONS[direction];

      // a fresh graph per pass: reusing one instance leaves removed nodes and
      // edges behind, and they keep influencing later layouts
      const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(
        () => ({})
      );

      dagreGraph.setGraph({
        rankdir: direction,
        nodesep: 100,
        ranksep: 170,
      });

      inNodes.forEach((node) => {
        dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
      });

      inEdges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
      });

      dagre.layout(dagreGraph);

      return {
        nodes: inNodes.map((node) => {
          const p = dagreGraph.node(node.id);
          return {
            ...node,
            sourcePosition: handles.source,
            targetPosition: handles.target,
            position: { x: p.x - nodeWidth / 2, y: p.y - nodeHeight / 2 },
          };
        }),
        edges: inEdges,
      };
    },
    []
  );

  useEffect(() => {
    if (!fetchedorg) return;

    if (!fetchedorg.nodes?.length) {
      setNodes([]);
      setEdges([]);
      return;
    }

    const position = { x: 0, y: 2 };
    const focus_profile_id = fetchedorg.focus_profile_id;

    const nodesData = fetchedorg.nodes.map(({ id, data, type }: any) => ({
      id: String(id),
      type: "custom",
      data: {
        id,
        icon: type,
        label: data.display_name,
        registration_number: data.registration_number,
        entity_type: data.entity_type,
        profile_status: data.profile_status?.status,
        profile_label: data.profile_status?.label,
        is_focus: Number(id) === Number(focus_profile_id),
      },
      position,
    }));

    const edgesData = (fetchedorg.edges ?? []).map(
      ({ id, source, target, data }: any) => {
        const relationships = data?.relationships ?? [];
        /*
         * A connection groups several relationships and the chart draws one
         * line, so it can only be ended when every relationship on it is. One
         * live relationship keeps the connection live.
         */
        const isEnded =
          relationships.length > 0 &&
          relationships.every((relationship: any) => !relationship.active);

        return {
          id: String(id),
          source: String(source),
          target: String(target),
          type: "custom",
          animated: !isEnded,
          // the arrowhead sits at the target end, so direction reads
          // company >> related party. Kept small: it only needs to indicate
          // direction, not draw attention away from the cards.
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 11,
            height: 11,
            color: EDGE_COLOUR,
          },
          // ended relationships are drawn dashed, per the VPF-010 line styles
          style: isEnded ? { strokeDasharray: "6 4" } : undefined,
          data: {
            endLabel: data?.display_label,
            relationships,
            isEnded,
          },
        };
      }
    );

    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      nodesData,
      edgesData,
      orientation
    );

    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
    // rebuilding on an orientation change re-runs the layout from the source
    // data, which is what makes the choice survive a refetch
  }, [fetchedorg, orientation, getLayoutedElements]);

  const nodeTypes = useMemo(() => ({ custom: CustomNode }), []);
  const edgeTypes = useMemo<EdgeTypes>(() => ({ custom: CustomEdge }), []);

  const onNodesChange = useCallback(
    (changes: any) => setNodes((nds) => applyNodeChanges(changes, nds)),
    []
  );

  // matched on the node id, the same identifier the is_focus flag above uses.
  // shaped like the node data the menu passes to the relationship pane, so the
  // empty state can open that pane for the focus profile
  const focusNodeData = useMemo(() => {
    const raw =
      fetchedorg?.nodes?.find(
        (node: any) =>
          String(node.id) === String(fetchedorg?.focus_profile_id) ||
          String(node.data?.profile_id) === String(fetchedorg?.focus_profile_id)
      ) ?? fetchedorg?.nodes?.[0];

    if (!raw) return null;

    return {
      id: raw.id,
      icon: raw.type,
      label: raw.data?.display_name,
      entity_type: raw.data?.entity_type,
      registration_number: raw.data?.registration_number,
    };
  }, [fetchedorg]);

  const focus_profile_name = focusNodeData?.label;

  const onEdgesChange = useCallback(
    (changes: any) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  );

  // ids are memoised so the toolbar's re-measure effect does not fire on every
  // render, only when the set of nodes actually changes
  const nodeIds = useMemo(() => nodes.map((node) => node.id), [nodes]);

  const focusNodeId = focusNodeData ? String(focusNodeData.id) : null;

  const { nodes: displayNodes, edges: displayEdges } = useMemo(
    () => applyShowFilters(nodes, edges, showFilters, focusNodeId),
    [nodes, edges, showFilters, focusNodeId]
  );

  // search should never offer something the filters have hidden
  const searchableNodes = useMemo(
    () => displayNodes.filter((node) => !node.hidden),
    [displayNodes]
  );

  /*
   * Deduplicated by code, which is not optional.
   *
   * ProfileRelationshipType is scoped per entity type, so the catalogue holds
   * one row per code per entity type: "shareholder" exists six times, once for
   * Private Limited Company, Public Limited Company, SPV, SPE, PCC and ICC. The
   * graph filter matches on code rather than id, so those rows collapse to a
   * single option. Without this, selecting Shareholder produced six identical
   * chips, because react-select marks every option whose value is selected.
   */
  const relationshipTypeOptions = useMemo(() => {
    const list = Array.isArray(relationshipTypes)
      ? relationshipTypes
      : (relationshipTypes as any)?.results ?? [];

    const byCode = new Map<string, { label: string; value: string }>();

    list.forEach((type: any) => {
      if (!type?.code || byCode.has(type.code)) return;
      byCode.set(type.code, {
        label: type.name ?? type.code,
        value: type.code,
      });
    });

    // 48 distinct codes, so alphabetical beats catalogue order for finding one
    return Array.from(byCode.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [relationshipTypes]);

  // derived from the graph in hand, so only statuses actually present are
  // offered rather than every status the platform defines
  const statusOptions = useMemo(() => {
    const seen = new Map<string, string>();

    (fetchedorg?.nodes ?? []).forEach((node: any) => {
      const status = node.data?.profile_status?.status;
      if (status && !seen.has(status)) {
        seen.set(status, node.data?.profile_status?.label ?? status);
      }
    });

    return Array.from(seen, ([value, label]) => ({ label, value }));
  }, [fetchedorg]);

  const onSelectNode = useCallback((nodeId: string) => {
    setNodes((current) =>
      current.map((node) => ({ ...node, selected: node.id === nodeId }))
    );
  }, []);

  const onNodeClick = useCallback((_e: any, node: any) => {
    setOpenMenuNodeId(null);
    setSelectedProfileId(Number(node?.data?.id));
    setIsDetailsOpen(true);
  }, []);

  const onNodeContextMenu = useCallback(
    (event: any, node: any) => {
      // suppress the browser's own context menu so ours is the only one shown
      event.preventDefault();
      openMenu(node.id);
    },
    [openMenu]
  );

  const onPaneClick = useCallback(() => setOpenMenuNodeId(null), []);

  const closeSlider = useCallback(() => {
    setIsDetailsOpen(false);
    setSelectedProfileId(null);
  }, []);

  if (!Number.isFinite(profileId)) {
    return (
      <Flex justifyContent="center" alignItems="center" minHeight="40vh">
        <div>Invalid profile id</div>
      </Flex>
    );
  }

  /*
   * Only the first load blanks the view. A background refetch keeps the chart
   * and the controls mounted and shows a small indicator instead: load filters
   * refetch, so unmounting here would make the filter panel disappear from
   * under the user every time they changed one.
   */
  if (orgQuery.isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" minHeight="40vh">
        <Loader />
      </Flex>
    );
  }

  if (orgQuery.isError) {
    return (
      <Flex justifyContent="center" alignItems="center" minHeight="40vh">
        <div>Failed to load organisation chart</div>
      </Flex>
    );
  }

  /*
   * The canvas is always rendered, even with nothing to draw.
   *
   * It used to be swapped out for a full-page empty state whenever there were no
   * connections, but a load filter that matches nothing produces exactly that,
   * so filtering by relationship type took the filter panel away with it and
   * left no way back. The focus profile is always returned by the graph, so
   * there is always at least one card to show; anything else is said in an
   * overlay.
   */
  const hasRelationships = edges.length > 0;
  const isFiltered = hasActiveLoadFilters(loadFilters);

  const filtersActive =
    hasActiveLoadFilters(loadFilters) || hasActiveShowFilters(showFilters);

  return (
    <OrgChartMenuContext.Provider value={menuContextValue}>
      {/*
       * The provider is what lets the sidebar sit outside <ReactFlow> and still
       * use useReactFlow for search centring.
       */}
      <ReactFlowProvider>
        <div
          ref={setChartEl}
          className="org-chart-layout"
          style={{ height: chartHeight ? `${chartHeight}px` : "70vh" }}
        >
          <ChartControlsSidebar
            isOpen={isSidebarOpen}
            loadFilters={loadFilters}
            onLoadFiltersChange={setLoadFilters}
            showFilters={showFilters}
            onShowFiltersChange={setShowFilters}
            searchableNodes={searchableNodes}
            relationshipTypeOptions={relationshipTypeOptions}
            statusOptions={statusOptions}
            onSelectNode={onSelectNode}
          />

          <div className="org-chart-canvas">
            <ReactFlow
              nodes={displayNodes}
              edges={displayEdges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              snapToGrid
              onNodeClick={onNodeClick}
              onNodeContextMenu={onNodeContextMenu}
              onPaneClick={onPaneClick}
              edgeTypes={edgeTypes}
              nodeTypes={nodeTypes}
              nodesConnectable={false}
              edgesReconnectable={false}
              minZoom={0}
              fitView
            >
              <ChartToolbar
                orientation={orientation}
                onOrientationChange={setOrientation}
                nodeIds={nodeIds}
                focusProfileName={focus_profile_name}
              />
              {/* the single control for the sidebar, on the whiteboard */}
              <Panel position="top-left">
                <button
                  type="button"
                  className="org-panel-trigger nodrag nopan"
                  title={isSidebarOpen ? "Hide filters" : "Find and filter"}
                  aria-label={
                    isSidebarOpen
                      ? "Hide find and filter"
                      : "Show find and filter"
                  }
                  aria-expanded={isSidebarOpen}
                  onClick={() => setIsSidebarOpen((open) => !open)}
                >
                  <FaSlidersH size={14} />
                  {!isSidebarOpen && filtersActive && (
                    <span className="org-panel-dot" />
                  )}
                </button>
              </Panel>
              {!orgQuery.isFetching && (
                <Panel position="bottom-center">
                  {/* three different reasons the chart can look empty, and they
                    need different answers */}
                  {!hasRelationships && !isFiltered && (
                    <Box className="org-chart-note">
                      <Box fontWeight={600} mb={1}>
                        No relationships yet
                      </Box>
                      <Box color="gray.50" mb={2}>
                        This profile is not linked to any other profiles.
                      </Box>
                      <Button
                        size="small"
                        isDisabled={!focusNodeData}
                        onClick={() =>
                          focusNodeData && openAddRelationship(focusNodeData)
                        }
                      >
                        Add relationship
                      </Button>
                    </Box>
                  )}
                  {!hasRelationships && isFiltered && (
                    <Box className="org-chart-note" color="gray.50">
                      No relationships match the current filters
                    </Box>
                  )}
                  {/* the focus profile is never hidden, so one remaining card
                    means the show filters excluded every related party */}
                  {hasRelationships &&
                    nodes.length > 1 &&
                    searchableNodes.length <= 1 && (
                      <Box className="org-chart-note" color="gray.50">
                        Every related party is hidden by the current filters
                      </Box>
                    )}
                </Panel>
              )}
              {orgQuery.isFetching && (
                <Panel position="top-center">
                  <Box className="org-chart-note" color="gray.50">
                    Updating chart...
                  </Box>
                </Panel>
              )}
              <Background bgColor="white" />
              {/* zoom and fit-to-view. Kept clear of the filter sidebar, which
                caps its own height to leave this corner free */}
              <Controls showInteractive={false} />
              <MiniMap
                pannable
                zoomable
                nodeColor={(node: any) =>
                  node.data?.icon === "entity" ? "#1a73e8" : "#5f6368"
                }
              />
            </ReactFlow>
          </div>
        </div>
      </ReactFlowProvider>

      <Slider isOpen={isDetailsOpen} onRequestClose={closeSlider} width="900px">
        <Box m={4}>
          {selectedProfileQuery.isFetching ? (
            <Loader />
          ) : (
            <ProfileTab profile={selectedProfile} />
          )}
        </Box>
      </Slider>

      {/* one instance for the whole chart, rather than one mounted per node */}
      <RelationshipSlider
        addRelationshipBoxOpen={!!relationshipProfile}
        setAddRelationshipBoxOpen={(value: boolean) =>
          !value && setRelationshipProfile(null)
        }
        firstProfile={relationshipProfile}
      />
    </OrgChartMenuContext.Provider>
  );
};
